import { useQuery } from '@tanstack/react-query'

import { apiClient } from '../apiClient'

export const PLAN_OVERVIEW_KEY = 'plan-overview-list'

type PlanOverviewParams = {
  cursor: number
  limit: number
  orderBy: string
  sortDescending?: boolean
  programIds?: string[]
  trainingProgramIds?: string[]
  operatorNameSearch?: string
  isFullTime?: boolean
  isWorkforceManaged?: boolean
}

export type OperatorProgramInstanceEligibility = 'none' | 'live_only' | 'recycler_only' | 'live_and_recycler'

export type OperatorProgramInstance = {
  programInstanceId?: string | null
  programInstanceName: string | null
  programGroupId: string
  programGroupName: string
  liveProgramId: string
  recyclerProgramId: string | null
  superMajorityProgramId?: string | null
  superMajorityEligible?: boolean
  displayName: string | null
  color: string | null
  iconType: string | null
  eligibility: OperatorProgramInstanceEligibility
  liveEligible: boolean
  recyclerEligible: boolean
  recyclerTrainingProgramId: string | null
  recyclerTrainingAssigned: boolean
  recyclerTrainingCertified: boolean
  recyclerTrainingProgress: number | null
  recyclerTrainingCompleted: boolean
  paused?: boolean | null
}

type OperatorPlanOverviewResponse = {
  programs: {
    programId: string
    displayName: string | null
    color: string
    iconType: string | null
    targetAccuracy?: number
  }[]
  operators: {
    operatorId: string
    name: string | null
    email: string
    avatarUrl: string | null
    dateCreated: string
    programIds: string[] | null
    certifiedTrainingProgramIds: string[] | null
    trainingProgramIds:
      | {
          programId: string
          progress: number
        }[]
      | null
    programInstances?: OperatorProgramInstance[]
    isFullTime: boolean
    isWorkforceManaged: boolean
  }[]
  total: number
}

export function useGetPlanOverview(params: PlanOverviewParams, enabled = true) {
  const normalizedOperatorNameSearch = params.operatorNameSearch?.trim() || undefined
  const normalizedParams = {
    ...params,
    operatorNameSearch: normalizedOperatorNameSearch,
  }

  return useQuery({
    enabled,
    queryKey: [PLAN_OVERVIEW_KEY, normalizedParams],
    queryFn: async () => {
      const programIds = normalizeIds(params.programIds)
      const trainingProgramIds = normalizeIds(params.trainingProgramIds)

      const { data } = await apiClient.get<OperatorPlanOverviewResponse>(`/int/v3/operators/plan-overview`, {
        params: {
          ...normalizedParams,
          programIds,
          trainingProgramIds,
        },
      })

      return data
    },
    refetchOnWindowFocus: false,
    select(data) {
      return {
        ...data,
        programs: [...data.programs].sort((a, b) => (a.displayName ?? '').localeCompare(b.displayName ?? '')),
      }
    },
  })
}

const normalizeIds = (ids: string[] | undefined) => {
  const normalizedIds = typeof ids === 'string' ? (ids as string).split(',') : ids
  return normalizedIds?.includes('null') ? 'null' : normalizedIds
}
